// FormikInput.tsx
import { MenuItem, Select, Checkbox, ListItemText } from "@mui/material";
import { useField, FieldInputProps, FieldMetaProps } from "formik";
import Input from "themeComponents/Input";
import Typography from "themeComponents/Typography";

interface FormikInputProps {
  name: string;
  label?: string;
  type?: string;
  placeholder?: string;
  size?: string;
  options?: Array<{ value?: string; _id?: string; label?: string; [key: string]: any }>;
  optionsLabel?: string;
  [key: string]: any;
}

const getOptValue = (opt: any) => opt?.value ?? opt?._id ?? "";
const getOptLabel = (opt: any, optionsLabel?: string) =>
  opt?.label ?? opt?.[optionsLabel || "name"] ?? String(getOptValue(opt));

const FormikInput = ({ name, label, ...props }: FormikInputProps): JSX.Element => {
  const [field, meta] = useField(name);
  const fieldProps = field as FieldInputProps<any>;
  const metaProps = meta as FieldMetaProps<any>;

  const size =
    props.size === "small" || props.size === "medium" ? props.size : undefined;

  const fieldLabel =
    label != null && label !== "" ? (
      <label
        htmlFor={fieldProps.name}
        style={{ display: "block", marginBottom: 4, cursor: "pointer" }}
      >
        <Typography variant="caption" fontWeight="bold">
          {label}
        </Typography>
      </label>
    ) : null;

  const hasError = metaProps.touched && Boolean(metaProps.error);

  const options =
    props.options || [
      { value: "option1", label: "Option 1" },
      { value: "option2", label: "Option 2" },
      { value: "option3", label: "Option 3" },
    ];

  if (props.type === "dropdown") {
    return (
      <>
        {fieldLabel}
        <Select
          labelId={`${fieldProps.name}-label`}
          id={fieldProps.name}
          {...fieldProps}
          {...props}
          size={size}
          displayEmpty
          sx={{
            borderColor: hasError ? "red" : "",
            ".MuiSelect-select": {
              width: "100%",
              display: "flex",
              alignItems: "center",
            },
          }}
          fullWidth
          renderValue={(selected) => {
            if (selected === "" || selected === undefined || selected === null) {
              return (
                <em style={{ color: "#9e9e9e" }}>
                  {props.placeholder || "-- Please Select --"}
                </em>
              );
            }
            const opt = options.find(
              (o) => getOptValue(o) === selected
            );
            return opt ? getOptLabel(opt, props.optionsLabel) : selected;
          }}
          MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
        >
          <MenuItem value="">
            <em>{props.placeholder || "-- Please Select --"}</em>
          </MenuItem>

          {options.map((opt) => (
            <MenuItem key={getOptValue(opt)} value={getOptValue(opt)}>
              {getOptLabel(opt, props.optionsLabel)}
            </MenuItem>
          ))}
        </Select>

        {metaProps.touched && metaProps.error ? (
          <div style={{ color: "red", fontSize: "0.8rem", marginTop: "4px" }}>
            {metaProps.error}
          </div>
        ) : null}
      </>
    );
  }

  if (props.type === "multiselect-dropdown") {
    const selectedValues: string[] = Array.isArray(fieldProps.value)
      ? fieldProps.value
      : [];

    return (
      <>
        {fieldLabel}
        <Select
          labelId={`${fieldProps.name}-label`}
          id={fieldProps.name}
          multiple
          value={selectedValues}
          onChange={(e) => {
            const val = e.target.value;
            // MUI can return string[] or string (autofill) -> normalize
            const next = Array.isArray(val) ? val : String(val).split(",");
            fieldProps.onChange({
              target: { name: fieldProps.name, value: next },
            } as any);
          }}
          onBlur={fieldProps.onBlur}
          {...props}
          size={size}
          displayEmpty
          fullWidth
          renderValue={(selected) => {
            const arr = Array.isArray(selected) ? selected : [];
            if (arr.length === 0) {
              return (
                <em style={{ color: "#9e9e9e" }}>
                  {props.placeholder || "-- Please Select --"}
                </em>
              );
            }

            const labels = arr
              .map((v) => {
                const opt = options.find((o) => getOptValue(o) === v);
                return opt ? getOptLabel(opt, props.optionsLabel) : v;
              })
              .filter(Boolean);

            return labels.join(", ");
          }}
          sx={{
            borderColor: hasError ? "red" : "",
            ".MuiSelect-select": {
              width: "100%",
              display: "flex",
              alignItems: "center",
            },
            
          }}
          MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
        >
          {/* placeholder item (disabled) */}
          <MenuItem disabled value="" >
            <em>{props.placeholder || "-- Please Select --"}</em>
          </MenuItem>

          {options.map((opt) => {
            const v = getOptValue(opt);
            const label = getOptLabel(opt, props.optionsLabel);
            const checked = selectedValues.includes(v);

            return (
              <MenuItem key={v} value={v}>
                <Checkbox checked={checked} />
                <ListItemText primary={label} />
              </MenuItem>
            );
          })}
        </Select>

        {metaProps.touched && metaProps.error ? (
          <div style={{ color: "red", fontSize: "0.8rem", marginTop: "4px" }}>
            {metaProps.error}
          </div>
        ) : null}
      </>
    );
  }

  // default input
  return (
    <>
      {fieldLabel}
      <Input
        id={fieldProps.name}
        {...fieldProps}
        {...props}
        {...({
          error: metaProps.touched && Boolean(metaProps.error),
          success: metaProps.touched && !metaProps.error,
        } as any)}
      />
      {metaProps.touched && metaProps.error ? (
        <div style={{ color: "red", fontSize: "0.8rem", marginTop: "4px" }}>
          {metaProps.error}
        </div>
      ) : null}
    </>
  );
};

export default FormikInput;
